import { computed, inject, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import { WSInstance, WSResponse } from '@/services/ws/utils/types'
import type { AIChatMessageData } from '../utils/types'
import { AccountCandidate, AccountSuggestion } from './utils/types'
import { AIType } from '../utils/enums'

import { generateId } from '@/entities/utils/helpers'
import { handleCatchedError } from '@/helpers/common'
import { createSuggestionTimeout } from '../utils/timeout'
import { prepareMessages } from '../utils/helpers'
import { readSuggestion } from './utils/helpers'

import { useUserStore } from '@/store/user'
import { pick } from 'lodash'

/**
 * The assistant behind the ownership step's suggested account.
 *
 * It is handed the user's own sentence and the accounts the repository actually
 * has — already narrowed to the types the wizard's transaction accepts — and
 * answers with one of them, or with nothing. One shot, like the wizard's own
 * read: there is no thread to restore and nothing worth persisting.
 *
 * A suggestion is an aid, never a requirement, so every failure here is silent
 * to the user: no toast, no error state, no suggested row. Failures are still
 * reported through handleCatchedError, so Faro sees them.
 */
export const useAIAccountSuggestionStore = defineStore('ai-account-suggestion', () => {
  // INIT
  const userStore = useUserStore()

  const ws = inject<WSInstance>('ws')

  const requestId = ref(`tmp_${generateId()}`)

  const suggestion = ref<AccountSuggestion>()
  const loading = ref(false)

  /**
   * Whether this account has been asked for at all, regardless of the answer.
   * The wizard no longer asks on its own — the user asks by clicking "Ask AI"
   * — so this is what tells that control to stop offering itself once it has
   * done its job.
   */
  const asked = ref(false)

  /*
   * The candidates the live request was sent with. An answer names an id, and
   * the only ids this store will accept are the ones it offered — a model that
   * returns anything else is answering about an account nobody has.
   */
  let candidateIds: string[] = []

  /** What has already been asked, so walking back into the step does not re-ask. */
  let askedFor = ''

  /*
   * A websocket that goes quiet leaves `loading` true, and the wait is drawn on
   * screen — a pulse that never resolves reads as the assistant still thinking
   * about a question nobody is going to answer.
   */
  const timeout = createSuggestionTimeout()

  // GETTERS

  const getSuggestion = computed(() => suggestion.value)

  const isLoading = computed(() => loading.value)

  const hasAsked = computed(() => asked.value)

  // ACTIONS

  const reset = () => {
    requestId.value = `tmp_${generateId()}`
    suggestion.value = undefined
    timeout.stop()
    loading.value = false
    asked.value = false
    candidateIds = []
    askedFor = ''
  }

  /**
   * Asks which of `accounts` the described record belongs to.
   *
   * Does nothing without a sentence: with no words to go on there is no context
   * to choose from, and a guess drawn from the account list alone would be the
   * wizard inventing an owner.
   */
  const suggest = async (params: {
    sentence: string
    flow: string
    transactionType: string
    accounts: AccountCandidate[]
  }) => {
    const { sentence, flow, transactionType, accounts } = params

    if (!sentence.trim() || !accounts.length) return
    // The websocket is absent when VITE_APP_WS_DISABLED is set.
    if (!ws) return

    /*
     * Keyed on everything the answer depends on. Re-asking the same question
     * would spend a model call to receive the answer already on screen.
     */
    const key = `${flow}|${transactionType}|${sentence}|${accounts.map(item => item.id).join(',')}`
    if (key === askedFor) return

    askedFor = key
    asked.value = true
    requestId.value = `tmp_${generateId()}`
    candidateIds = accounts.map(account => account.id)
    suggestion.value = undefined
    loading.value = true

    /*
     * Asking again is allowed after a timeout — the question was never
     * answered, so the key is dropped with the wait.
     */
    timeout.start(() => {
      loading.value = false
      askedFor = ''
      handleCatchedError('Account suggestion timed out — no answer for the account')
    })

    const { messages } = prepareMessages(sentence)

    const messageData: AIChatMessageData = {
      ai_type: AIType.ACCOUNT_SUGGESTION,
      messages,
      dynamic_context: {
        flow,
        transaction_type: transactionType,
        /*
         * Quoted the way the asset wizard's prompt quotes accounts, so the two
         * assistants read the repository in the same shape. The id is included
         * because the answer names one.
         */
        accounts_list: JSON.stringify(accounts
          .map(item => pick(item, ['id', 'name', 'ticker', 'type']))
        ),
      },
    }

    ws.send('ai', 'chat', requestId.value, messageData)
  }

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value) return
      ws?.subscribe(
        'ai',
        'chat',
        (message: WSResponse<string>, responseId: string) => {
          // Every assistant shares this channel; the request id is what says
          // the answer is ours.
          if (responseId !== requestId.value) return

          try {
            if (message.errors || !message.data) {
              handleCatchedError(
                `Account suggestion failed: ${message.errors?.join(', ') ?? 'empty answer'}`,
              )
              return
            }

            const { suggestion: read, error } = readSuggestion(message.data, candidateIds)

            if (error) {
              handleCatchedError(error)
              return
            }

            // Absent when the answer was "nothing fits", which is not an error.
            if (read) suggestion.value = read
          } catch (e) {
            handleCatchedError(e as string)
          } finally {
            timeout.stop()
            loading.value = false
          }
        },
      )
    },
    { immediate: true },
  )

  return {
    isLoading,
    hasAsked,

    getSuggestion,

    suggest,
    reset,
  }
})
